import { getInitials } from '../../lib/initials';
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { createEvent, type CreateEventInput } from '../../services/events';
import { uploadImage } from '../../services/images';
import { getAllUsers } from '../../services/users';
import { useAuth } from '../../hooks/useAuth';

/** Convert a UTC Date to a local datetime string for <input type="datetime-local"> */
const toLocalDateTimeInput = (date: Date): string => {
  const offset = date.getTimezoneOffset();
  const local = new Date(date.getTime() - offset * 60 * 1000);
  return local.toISOString().slice(0, 16);
};

export default function CreateEventForm() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [isClanMeeting, setIsClanMeeting] = useState(false);
  const [location, setLocation] = useState('');
  const [startDateTime, setStartDateTime] = useState('');
  const [signupDeadline, setSignupDeadline] = useState('');
  const [signupDeadlineTouched, setSignupDeadlineTouched] = useState(false);
  const [endDateTime, setEndDateTime] = useState('');
  const [endTimeTouched, setEndTimeTouched] = useState(false);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [selectedOrganizerIds, setSelectedOrganizerIds] = useState<string[]>(
    user?.id ? [user.id] : []
  );
  const [error, setError] = useState('');

  const { data: users = [] } = useQuery({
    queryKey: ['users'],
    queryFn: getAllUsers,
  });

  useEffect(() => {
    if (user?.id) {
      setSelectedOrganizerIds((prev) =>
        prev.includes(user.id) ? prev : [...prev, user.id]
      );
    }
  }, [user?.id]);

  useEffect(() => {
    if (!startDateTime || signupDeadlineTouched) return;
    const start = new Date(startDateTime);
    const def = new Date(start.getTime() - 7 * 24 * 60 * 60 * 1000);
    setSignupDeadline(toLocalDateTimeInput(def));
  }, [startDateTime, signupDeadlineTouched]);

  useEffect(() => {
    if (!startDateTime || endTimeTouched) return;
    const start = new Date(startDateTime);
    const def = new Date(start.getTime() + 3 * 60 * 60 * 1000);
    setEndDateTime(toLocalDateTimeInput(def));
  }, [startDateTime, endTimeTouched]);

  const createEventMutation = useMutation({
    mutationFn: async (input: CreateEventInput) => {
      // Upload image if selected
      let imageUrl: string | undefined;
      if (selectedImage) {
        imageUrl = await uploadImage(selectedImage, 'events');
      }

      return createEvent({
        ...input,
        imageUrl,
      });
    },
    onSuccess: (event) => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
      navigate(`/events/${event.id}`);
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || 'Kunne ikke oprette begivenhed');
    },
  });

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setError('Filen skal være et billede');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setError('Billedet må maksimalt være 5MB');
        return;
      }
      setSelectedImage(file);
      setImagePreview(URL.createObjectURL(file));
      setError('');
    }
  };

  const removeImage = () => {
    setSelectedImage(null);
    if (imagePreview) {
      URL.revokeObjectURL(imagePreview);
      setImagePreview(null);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!title.trim()) {
      setError('Titel er påkrævet');
      return;
    }

    if (!description.trim()) {
      setError('Beskrivelse er påkrævet');
      return;
    }

    if (!startDateTime) {
      setError('Starttidspunkt er påkrævet');
      return;
    }

    if (selectedOrganizerIds.length === 0) {
      setError('Vælg mindst én arrangør');
      return;
    }

    const start = new Date(startDateTime);
    if (start < new Date()) {
      setError('Starttidspunkt skal være i fremtiden');
      return;
    }

    const defaultDeadline = new Date(start.getTime() - 7 * 24 * 60 * 60 * 1000);
    const deadline = signupDeadline ? new Date(signupDeadline) : defaultDeadline;
    if (deadline >= start) {
      setError('Tilmeldingsfrist skal være før starttidspunkt');
      return;
    }

    const end = endDateTime ? new Date(endDateTime) : undefined;
    if (end && end <= start) {
      setError('Sluttidspunkt skal være efter starttidspunkt');
      return;
    }

    createEventMutation.mutate({
      title: title.trim(),
      description: description.trim(),
      isClanMeeting,
      location: location.trim() || undefined,
      startTime: start.toISOString(),
      signupDeadline: deadline.toISOString(),
      endTime: end?.toISOString(),
      organizerIds: selectedOrganizerIds,
    });
  };

  const toggleOrganizer = (userId: string) => {
    setSelectedOrganizerIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4">
          {error}
        </div>
      )}

      {/* Title */}
      <div>
        <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
          Titel *
        </label>
        <input
          type="text"
          id="title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="input"
          placeholder="Fx. Klanmøde i skoven"
          maxLength={200}
          required
        />
      </div>

      {/* Description */}
      <div>
        <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
          Beskrivelse *
        </label>
        <textarea
          id="description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="input min-h-[120px]"
          placeholder="Beskriv hvad begivenheden går ud på..."
          maxLength={5000}
          required
        />
        <div className="text-xs text-gray-500 mt-1">
          {description.length}/5000 tegn
        </div>
      </div>

      {/* Location */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Type
        </label>
        <label className="inline-flex items-center gap-2 text-sm text-gray-800 cursor-pointer">
          <input
            type="checkbox"
            checked={isClanMeeting}
            onChange={(e) => setIsClanMeeting(e.target.checked)}
            className="h-4 w-4 text-primary-600 border-gray-300 rounded"
          />
          Klanmøde
        </label>
      </div>

      {/* Location */}
      <div>
        <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-1">
          Sted
        </label>
        <input
          type="text"
          id="location"
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          className="input"
          placeholder="Fx. Amager Strandpark"
          maxLength={500}
        />
      </div>

      {/* Start datetime */}
      <div>
        <label htmlFor="startDateTime" className="block text-sm font-medium text-gray-700 mb-1">
          Starttidspunkt *
        </label>
        <input
          type="datetime-local"
          id="startDateTime"
          value={startDateTime}
          onChange={(e) => setStartDateTime(e.target.value)}
          className="input"
          required
        />
      </div>

      {/* End datetime */}
      <div>
        <label htmlFor="endDateTime" className="block text-sm font-medium text-gray-700 mb-1">
          Sluttidspunkt
        </label>
        <input
          type="datetime-local"
          id="endDateTime"
          value={endDateTime}
          onChange={(e) => { setEndDateTime(e.target.value); setEndTimeTouched(true); }}
          className="input"
        />
      </div>

      {/* Signup deadline */}
      <div>
        <label htmlFor="signupDeadline" className="block text-sm font-medium text-gray-700 mb-1">
          Tilmeldingsfrist
        </label>
        <input
          type="datetime-local"
          id="signupDeadline"
          value={signupDeadline}
          onChange={(e) => { setSignupDeadline(e.target.value); setSignupDeadlineTouched(true); }}
          className="input"
        />
      </div>

      {/* Organizers */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Arrangører *
        </label>
        <div className="max-h-56 overflow-y-auto border border-gray-200 rounded-lg p-3 space-y-2 bg-white">
          {users.map((member) => (
            <label key={member.id} className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={selectedOrganizerIds.includes(member.id)}
                onChange={() => toggleOrganizer(member.id)}
                className="h-4 w-4 text-primary-600 border-gray-300 rounded"
              />
              {member.profileImageUrl ? (
                <img
                  src={member.profileImageUrl}
                  alt={member.name}
                  className="w-7 h-7 rounded-full object-cover"
                />
              ) : (
                <div className="w-7 h-7 bg-gray-300 rounded-full flex items-center justify-center text-xs font-semibold text-gray-700">
                  {getInitials(member.name)}
                </div>
              )}
              <span className="text-sm text-gray-800">{member.name}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Image upload */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Billede
        </label>
        
        {imagePreview ? (
          <div className="relative">
            <img
              src={imagePreview}
              alt="Preview"
              className="w-full h-64 object-cover rounded-lg"
            />
            <button
              type="button"
              onClick={removeImage}
              className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-2 hover:bg-red-600 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        ) : (
          <label className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-blue-500 transition-colors block">
            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              className="hidden"
            />
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <p className="mt-2 text-sm text-gray-600">
              Klik for at uploade et billede
            </p>
            <p className="text-xs text-gray-500">
              PNG, JPG, GIF op til 5MB
            </p>
          </label>
        )}
      </div>

      {/* Submit buttons */}
      <div className="flex gap-3">
        <button
          type="submit"
          disabled={createEventMutation.isPending}
          className="btn-primary flex-1 disabled:opacity-50"
        >
          {createEventMutation.isPending ? 'Opretter...' : 'Opret begivenhed'}
        </button>
        <button
          type="button"
          onClick={() => navigate('/events')}
          className="btn-secondary"
        >
          Annuller
        </button>
      </div>
    </form>
  );
}
